import React, { useEffect, useState, useRef, Component } from 'react';
import { useInView } from 'react-intersection-observer';
import { MapPin, ArrowRight, Quote, Heart, ChevronDown } from 'lucide-react';
import { triggerSubtleConfetti } from '../utils/confetti';
// Story chapter type definition
type StoryChapter = {
  id: string;
  title: string;
  content: string[];
  image: string;
  location: string;
  year: string;
  pullQuote?: string;
};
const JourneyStory = () => {
  const [activeChapter, setActiveChapter] = useState(0);
  const [isMapAnimated, setIsMapAnimated] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  // Location data for the interactive map
  const journeyLocations = [{
    id: 'thailand',
    name: 'Phang Nga, Thailand',
    position: {
      top: '40%',
      left: '70%'
    },
    color: 'coral-pink',
    years: 'Childhood',
    title: 'Childhood Roots',
    description: 'Where my journey began among limestone karsts and emerald waters.',
    details: ['Early exposure to community healthcare', 'Cultural foundation in Thai values', 'Strong family and community bonds', 'Formative years in a kampung environment'],
    image: 'https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'
  }, {
    id: 'finland',
    name: 'Finland',
    position: {
      top: '20%',
      left: '55%'
    },
    color: 'sage-mint',
    years: 'Teen Years & Education',
    title: 'Education & Career',
    description: 'Where I developed my nursing expertise and clinical precision.',
    details: ['Rigorous Finnish healthcare training', 'Evidence-based practice foundation', 'Multiple clinical rotations', '20+ specialized certifications'],
    image: 'https://images.unsplash.com/photo-1476900543704-4312b78632f8?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'
  }, {
    id: 'singapore',
    name: 'Singapore',
    position: {
      top: '45%',
      left: '72%'
    },
    color: 'blush-rose',
    years: 'The Future',
    title: 'Future Home',
    description: 'Where East meets West - the perfect place to apply my multicultural healthcare approach.',
    details: ['Ideal healthcare environment', 'Multicultural patient population', 'Bringing Finnish expertise to Asia', 'Combining precision with cultural sensitivity'],
    image: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'
  }];
  // Story chapters data
  const chapters: StoryChapter[] = [{
    id: 'opening',
    title: 'The Two Worlds That Made Me',
    content: ["Imagine a twelve-year-old girl, feet dangling in the emerald waters of Phang Nga Bay, watching limestone karsts pierce through morning mist like ancient guardians. That girl was me—a kampung child whose playground was Thailand's most dramatic coastline, where every tide brought new adventures and every sunset painted stories across the Andaman Sea.", "I never imagined that one day, I'd trade those warm waters for Finnish snow, or that the journey between these two worlds would shape me into the healer I am today."],
    image: 'https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
    location: 'Phang Nga, Thailand',
    year: 'Childhood',
    pullQuote: 'My story begins where the Andaman Sea meets the sky...'
  }, {
    id: 'roots',
    title: 'Roots in Two Soils',
    content: ["In Phang Nga, my childhood wasn't just shaped by the stunning limestone cliffs that tourists travelled thousands of miles to photograph—it was molded by the kampung spirit that ran deeper than those emerald waters. Here, healthcare wasn't just a service; it was neighbours checking on elderly aunties, grandmothers sharing traditional remedies, and the entire village becoming your extended family when someone fell ill.", "My Thai mother and Finnish father gave me something precious: a bridge between worlds before I even knew I'd need one. While my friends were learning one way to see the world, I was absorbing two—the warmth of Thai communal care and the precision of Nordic thinking.", "At twelve, my life transformed overnight. My mother, with the fierce determination only immigrant mothers possess, packed up her three daughters and flew us 8,000 kilometres north to Finland. She saw something we couldn't yet see: that the best education could open doors we didn't even know existed."],
    image: 'https://images.unsplash.com/photo-1528181304800-259b08848526?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
    location: 'Family Home',
    year: 'Ages 1-12',
    pullQuote: 'While my friends were learning one way to see the world, I was absorbing two...'
  }, {
    id: 'forest',
    title: 'Lessons from the Forest Floor',
    content: ["Lappeenranta welcomed us with sub-zero temperatures and eighteen-hour winter nights. The adjustment wasn't just cultural—it was existential. But my mother, ever resourceful, turned every challenge into a lesson.", "When money was tight (and it often was), summer became our secret weapon. While other families vacationed, we ventured into Finnish forests with empty buckets and determined hearts. Mustikka (blueberries), puolukka (lingonberries), lakka (cloudberries)—each had its season, its secret spots, its market value. My fingers, stained purple-blue, learned that dignity comes not from what you have, but from what you're willing to work for.", "Those forest expeditions taught me patience—the same patience I'd later need when sitting with elderly patients. They taught me to spot subtle differences—between ripe and almost-ripe, safe and poisonous—skills that would translate into detecting the slightest changes in a patient's condition. Most importantly, they taught me that caring for your family sometimes means getting your hands dirty, your back sore, and your heart full."],
    image: 'https://images.unsplash.com/photo-1476900543704-4312b78632f8?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
    location: 'Lappeenranta, Finland',
    year: 'Teen Years',
    pullQuote: 'My fingers learned in Finnish forests what my heart knew in Thai villages—caring means commitment'
  }, {
    id: 'healer',
    title: 'The Making of a Healer',
    content: ["Lahti became my launching pad into nursing. Here, in Finland's eighth-largest city, I discovered that my unique background wasn't a liability—it was my superpower. While my classmates saw patients, I saw people. My Thai upbringing had taught me to read the unspoken—the worried glance of a daughter, the proud resistance of an elderly man refusing help, the quiet fear behind brave words.", 'My Finnish education gave me the frameworks, the evidence-based practices, the rigorous standards. But it was the synthesis—the ability to deliver Nordic clinical excellence with Asian warmth—that set me apart. In psychiatric wards, I could sense the cultural shame that kept Asian patients from discussing mental health. In geriatric units, I understood both the Finnish desire for independence and the Asian expectation of family involvement.', "Between Lahti and Helsinki, visiting my sister, taking on different clinical rotations, each experience added another tool to my healing toolkit. Twenty certifications later, I wasn't just collecting credentials—I was building bridges between what healthcare could be and what patients needed it to be."],
    image: 'https://images.unsplash.com/photo-1584982751601-97dcc096659c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
    location: 'Lahti, Finland',
    year: 'Early Career',
    pullQuote: 'Twenty certifications in two years because good enough never is'
  }, {
    id: 'call',
    title: 'The Call Home',
    content: ["Throughout my Finnish years, I never severed my Asian roots. Annual pilgrimages back to Thailand weren't vacations—they were reconnections with the part of me that understood why patients' families brought home-cooked meals to hospitals, why elderly patients apologised for being a burden, why a gentle touch sometimes healed more than medication.", "When my grandfather—the man who taught me that strength could be gentle—suffered a stroke, everything crystallised. Watching him, once so vital, now bedbound, I saw the intersection of everything I'd learned. My sisters, mother, and I took turns flying back, each visit a reminder that healthcare isn't just about clinical excellence—it's about presence, about family, about understanding that healing happens in hearts as much as in bodies.", 'And then, life offered an unexpected gift: love that drew me to Singapore. My fiancé, based in this incredible city-state where East truly meets West, became another reason to return to Southeast Asian soil.'],
    image: 'https://images.unsplash.com/photo-1512678080530-7760d81faba6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
    location: 'Between Worlds',
    year: 'Recent Years',
    pullQuote: "Home isn't a place—it's where your skills meet their purpose"
  }, {
    id: 'singapore',
    title: 'Why Singapore, Why Now',
    content: ["Singapore represents everything I've been preparing for without knowing it. Here's a healthcare system that demands excellence (Finnish training: ✓), serves a multicultural population (cultural code-switching: ✓), values both innovation and tradition (my entire life story: ✓).", "When I look at Singapore's rapidly ageing population, I see my grandfather. When I read about the growing focus on mental health, I remember the psychiatric units in Lahti. When I learn about Singapore's nursing shortage, I don't see a crisis—I see an opportunity to contribute something unique.", 'The girl who once sold forest berries understands the value of hard work. The woman who earned twenty certifications in two years knows that excellence is a journey, not a destination. The nurse who speaks three languages knows that healing transcends words.'],
    image: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
    location: 'Singapore',
    year: 'The Future',
    pullQuote: "I'm not leaving Finland behind—I'm bringing its best with me"
  }, {
    id: 'promise',
    title: 'The Promise',
    content: ["Every sunrise in Phang Nga taught me hope. Every Finnish winter taught me resilience. Now, I'm ready to bring both to Singapore.", 'Europe gave me world-class training and unshakeable standards. Asia gave me heart and cultural intuition. My patients have given me purpose.', "I'm not just another foreign nurse seeking opportunities. I'm coming home—to warmer smiles, to familiar hierarchies of respect, to the kind of healthcare that sees families as partners, not visitors. I'm bringing with me the best of Finnish healthcare: the rigour, the evidence-based practice, the patient safety focus. But I'm delivering it with something extra: the understanding that comes from being raised by a kampung, shaped by forests, and guided by grandparents who taught me that caring for others is the highest calling.", "To Singapore's healthcare leaders: I offer you more than qualifications. I offer you someone who understands that excellent healthcare happens at the intersection of clinical expertise and cultural wisdom. Someone who's spent a lifetime preparing for exactly this moment, exactly this place, exactly this purpose."],
    image: 'https://images.unsplash.com/photo-1565967511849-76a60a516170?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
    location: 'The Journey Continues',
    year: 'Tomorrow',
    pullQuote: 'The limestone karsts of Phang Nga still stand, timeless and strong. The Finnish forests still offer their berries to those willing to work. And I stand ready, shaped by both landscapes.'
  }];
  // Create refs for each chapter
  const chapterRefs = chapters.map(() => {
    const {
      ref,
      inView
    } = useInView({
      threshold: 0.4,
      triggerOnce: false
    });
    return {
      ref,
      inView
    };
  });
  // Update active chapter based on scroll position
  useEffect(() => {
    const inViewIndex = chapterRefs.findIndex(chapter => chapter.inView);
    if (inViewIndex !== -1) {
      setActiveChapter(inViewIndex);
    }
  }, [chapterRefs.map(chapter => chapter.inView)]);
  // Handle timeline navigation
  const scrollToChapter = (index: number) => {
    const chapterRef = chapterRefs[index].ref;
    if (chapterRef.current) {
      // Add offset to account for sticky header
      const headerHeight = 72 + 48; // Navbar + timeline heights
      const elementPosition = chapterRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setActiveChapter(index);
    triggerSubtleConfetti(document.getElementById(`timeline-node-${index}`));
  };
  // Animate journey map when visible
  const {
    ref: mapRef,
    inView: mapInView
  } = useInView({
    threshold: 0.2,
    triggerOnce: true
  });
  useEffect(() => {
    if (mapInView) {
      setIsMapAnimated(true);
    }
  }, [mapInView]);
  // Function to handle location selection
  const handleLocationSelect = (locationId: string) => {
    setSelectedLocation(prev => prev === locationId ? null : locationId);
    triggerSubtleConfetti(document.getElementById(`marker-${locationId}`));
  };
  // Handle photo carousel navigation
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const photos = [{
    src: 'https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    caption: 'Childhood in Phang Nga Bay'
  }, {
    src: 'https://images.unsplash.com/photo-1476900543704-4312b78632f8?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    caption: 'Berry picking in Finnish forests'
  }, {
    src: 'https://images.unsplash.com/photo-1584982751601-97dcc096659c?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    caption: 'Nursing journey in Lahti'
  }, {
    src: 'https://images.unsplash.com/photo-1512678080530-7760d81faba6?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    caption: 'With grandfather in Thailand'
  }, {
    src: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    caption: 'Professional journey to Singapore'
  }];
  const nextPhoto = () => {
    setCurrentPhotoIndex(prevIndex => (prevIndex + 1) % photos.length);
  };
  const prevPhoto = () => {
    setCurrentPhotoIndex(prevIndex => (prevIndex - 1 + photos.length) % photos.length);
  };
  return <section id="about" className="bg-cream-white" data-id="element-463">
      {/* Hero section */}
      <div className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden" data-id="element-464">
        <div className="absolute inset-0 bg-gradient-to-r from-sage-mint/30 to-coral-pink/30" data-id="element-465"></div>
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center opacity-20" data-id="element-466"></div>
        <div className="relative z-10 text-center p-6 max-w-4xl" data-id="element-467">
          {/* Badge Component - matching the Hero.tsx style */}
          <div className="inline-flex items-center bg-sage-mint/10 border border-sage-mint rounded-full px-4 py-2 mb-6 hover:bg-sage-mint/20 transition-all duration-300 cursor-default" data-id="element-468">
            <span className="text-xs text-sage-mint font-medium" data-id="element-469">
              My Personal Journey
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-light text-charcoal mb-4" data-id="element-470">
            From Kampung Waters to Nordic{' '}
            <span className="font-semibold relative" data-id="element-471">
              Winters
              <span className="absolute bottom-0 left-0 w-full h-1 bg-coral-pink/30 rounded-full" data-id="element-472"></span>
            </span>
          </h1>
          <p className="text-base md:text-lg text-grey-600" data-id="element-473">
            The story of how Thailand and Finland shaped my approach to
            healthcare
          </p>
        </div>
      </div>
      {/* Timeline navigation */}
      <div ref={timelineRef} className="sticky top-[72px] bg-cream-white/95 backdrop-blur-nav z-30 border-b border-grey-400/20 shadow-sm py-3" data-id="element-474">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-475">
          <div className="relative" data-id="element-476">
            {/* Background track */}
            <div className="absolute h-[2px] top-1/2 left-0 right-0 bg-sage-mint/30" data-id="element-477"></div>
            {/* Progress indicator */}
            <div className="absolute h-[2px] top-1/2 left-0 bg-sage-mint transition-all duration-1000" style={{
            width: `${activeChapter / (chapters.length - 1) * 100}%`
          }} data-id="element-478"></div>
            {/* Timeline nodes */}
            <div className="relative flex justify-between" data-id="element-479">
              {chapters.map((chapter, index) => <div id={`timeline-node-${index}`} key={chapter.id} className="flex flex-col items-center cursor-pointer group" onClick={() => scrollToChapter(index)} data-id="element-480">
                  {/* Node circle */}
                  <div className={`
                      w-3 h-3 sm:w-4 sm:h-4 rounded-full z-10 transition-all duration-300
                      ${index <= activeChapter ? 'bg-sage-mint scale-100' : 'bg-grey-400/50 scale-75 group-hover:bg-sage-mint/50 group-hover:scale-90'}
                    `} data-id="element-481"></div>
                  {/* Year label - hidden on very small screens, visible on hover/active */}
                  <span className={`
                      hidden xs:block text-[10px] sm:text-xs mt-2 transition-all duration-300 whitespace-nowrap px-1
                      ${index === activeChapter ? 'text-sage-mint font-medium' : 'text-grey-600 group-hover:text-sage-mint/70'}
                    `} data-id="element-482">
                    {chapter.year}
                  </span>
                  {/* Mobile tooltip - only visible on very small screens when active */}
                  <div className={`
                      absolute top-6 bg-white shadow-md rounded-md py-1 px-2 text-[10px] z-20
                      xs:hidden transition-opacity duration-200
                      ${index === activeChapter ? 'opacity-100' : 'opacity-0 pointer-events-none'}
                    `} data-id="element-483">
                    {chapter.year}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
      {/* Story content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-484">
        <div className="space-y-24" data-id="element-485">
          {chapters.map((chapter, index) => <div key={chapter.id} ref={chapterRefs[index].ref} className={`scroll-mt-32 transition-opacity duration-700 ${Math.abs(activeChapter - index) <= 1 ? 'opacity-100' : 'opacity-50'}`} data-id="element-486">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center" data-id="element-487">
                <div className={`${index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'}`} data-id="element-488">
                  <div className="space-y-6" data-id="element-489">
                    {/* Location badge */}
                    <div className="inline-flex items-center bg-sage-mint/10 border border-sage-mint rounded-full px-4 py-2 hover:bg-sage-mint/20 transition-all duration-300 cursor-default" data-id="element-490">
                      <MapPin className="h-3 w-3 text-sage-mint mr-1" data-id="element-491" />
                      <span className="text-xs text-sage-mint font-medium" data-id="element-492">
                        {chapter.location}
                      </span>
                    </div>
                    {/* Chapter title */}
                    <h2 className="text-2xl md:text-3xl font-light text-charcoal" data-id="element-493">
                      {chapter.title}
                    </h2>
                    {/* Chapter content */}
                    <div className="space-y-4" data-id="element-494">
                      {chapter.content.map((paragraph, pIndex) => <p key={pIndex} className="text-grey-600 leading-relaxed" data-id="element-495">
                          {paragraph}
                        </p>)}
                    </div>
                    {/* Pull quote */}
                    {chapter.pullQuote && <div className="relative pl-6 border-l-2 border-coral-pink my-8" data-id="element-496">
                        <Quote className="absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 h-6 w-6 text-coral-pink bg-cream-white p-1" data-id="element-497" />
                        <p className="text-lg text-grey-600 italic" data-id="element-498">
                          "{chapter.pullQuote}"
                        </p>
                      </div>}
                  </div>
                </div>
                <div className={`${index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'}`} data-id="element-499">
                  <div className="liquid-glass-card rounded-2xl overflow-hidden shadow-card relative hover:shadow-glow-mint transition-all duration-500 group" data-id="element-500">
                    <div className="glass-reflection" data-id="element-501"></div>
                    <img src={chapter.image} alt={chapter.title} className="w-full h-auto object-cover transition-all duration-500 group-hover:scale-105" data-id="element-502" />
                    <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-charcoal/50 to-transparent" data-id="element-503"></div>
                    <div className="absolute bottom-4 left-4 right-4 text-white" data-id="element-504">
                      <p className="text-sm font-light" data-id="element-505">{chapter.year}</p>
                      <h3 className="text-lg font-medium" data-id="element-506">
                        {chapter.location}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
      {/* Enhanced 3D Journey Map */}
      <div ref={mapRef} className="py-16 bg-sage-mint/5" data-id="element-507">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-508">
          <h2 className="text-2xl md:text-3xl font-light text-charcoal mb-8 text-center" data-id="element-509">
            A Journey Across Continents
          </h2>
          <div className="liquid-glass-card bg-white/90 rounded-xl shadow-card relative overflow-hidden hover:shadow-glow-mint transition-all duration-300" data-id="element-510">
            <div className="glass-reflection" data-id="element-511"></div>
            {/* 3D-effect Map Background */}
            <div className="absolute inset-0 bg-cream-white" data-id="element-512">
              <div className="relative w-full h-full overflow-hidden transform perspective-1000" data-id="element-513">
                <img src="https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1500&q=80" alt="World map" className="w-full h-full object-cover opacity-80 scale-110 transform rotate-x-1" style={{
                transformStyle: 'preserve-3d'
              }} data-id="element-514" />
                <div className="absolute inset-0 bg-gradient-to-b from-white/40 to-transparent" data-id="element-515"></div>
                {/* Subtle topographical overlay for 3D effect */}
                <div className="absolute inset-0 opacity-10 mix-blend-overlay" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23BADFDB' fill-opacity='0.2' fill-rule='evenodd'/%3E%3C/svg%3E")`,
                backgroundSize: '100px 100px'
              }} data-id="element-516"></div>
              </div>
            </div>
            {/* Interactive Map Content with 3D-style containers */}
            <div className="relative h-[500px] p-6" data-id="element-517">
              {/* Journey paths with enhanced animation */}
              <svg className="absolute inset-0 w-full h-full z-0" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-518">
                {/* Thailand to Finland path - animated dashed line with arrow */}
                <path d={`M 70,40 Q 62.5,30 55,20`} fill="none" stroke="#BADFDB" strokeWidth="1.2" strokeDasharray="2 1" className="transition-all duration-1000" style={{
                strokeDashoffset: isMapAnimated ? 0 : 100,
                opacity: isMapAnimated ? 0.9 : 0,
                filter: 'drop-shadow(0 0 3px rgba(186, 223, 219, 0.6))',
                transition: 'stroke-dashoffset 1.8s ease-in-out, opacity 0.8s ease-in-out'
              }} data-id="element-519" />
                {/* Arrow marker for Thailand to Finland */}
                <circle cx="55" cy="20" r="0.8" fill="#BADFDB" style={{
                opacity: isMapAnimated ? 1 : 0,
                filter: 'drop-shadow(0 0 2px rgba(186, 223, 219, 0.8))',
                transition: 'opacity 0.5s ease-in-out 1.8s'
              }} data-id="element-520" />
                {/* Finland to Singapore path - animated dashed line with arrow */}
                <path d={`M 55,20 Q 63.5,32.5 72,45`} fill="none" stroke="#FFA4A4" strokeWidth="1.2" strokeDasharray="2 1" className="transition-all duration-1000" style={{
                strokeDashoffset: isMapAnimated ? 0 : 100,
                opacity: isMapAnimated ? 0.9 : 0,
                filter: 'drop-shadow(0 0 3px rgba(255, 164, 164, 0.6))',
                transition: 'stroke-dashoffset 1.8s ease-in-out 0.8s, opacity 0.8s ease-in-out 0.8s'
              }} data-id="element-521" />
                {/* Arrow marker for Finland to Singapore */}
                <circle cx="72" cy="45" r="0.8" fill="#FFA4A4" style={{
                opacity: isMapAnimated ? 1 : 0,
                filter: 'drop-shadow(0 0 2px rgba(255, 164, 164, 0.8))',
                transition: 'opacity 0.5s ease-in-out 2.6s'
              }} data-id="element-522" />
              </svg>
              {/* Location Markers - Enhanced with 3D effect and interactive states */}
              {journeyLocations.map(location => <div key={location.id} id={`marker-${location.id}`} className={`absolute cursor-pointer transition-all duration-300 z-10
                    ${selectedLocation === location.id ? 'scale-110' : 'hover:scale-105'}`} style={{
              top: location.position.top,
              left: location.position.left
            }} onClick={() => handleLocationSelect(location.id)} data-id="element-523">
                  {/* Marker with enhanced 3D effect */}
                  <div className="relative transform transition-transform duration-300" data-id="element-524">
                    {/* Pulse animation ring */}
                    <div className={`absolute w-12 h-12 rounded-full animate-ping opacity-50`} style={{
                  backgroundColor: `var(--${location.color})`,
                  animationDuration: '2s',
                  opacity: selectedLocation === location.id ? 0.7 : 0.3
                }} data-id="element-525"></div>
                    {/* Main marker circle with 3D effect */}
                    <div className={`relative w-10 h-10 rounded-full flex items-center justify-center shadow-lg transform transition-all duration-300 border-2 border-white`} style={{
                  backgroundColor: `var(--${location.color})`,
                  boxShadow: selectedLocation === location.id ? `0 0 15px 2px var(--${location.color})` : `0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)`
                }} data-id="element-526">
                      {/* Location number */}
                      <span className="text-white font-bold text-sm" data-id="element-527">
                        {location.id === 'thailand' ? '1' : location.id === 'finland' ? '2' : '3'}
                      </span>
                      {/* Small indicator dot for active state */}
                      {selectedLocation === location.id && <div className="absolute top-0 right-0 w-3 h-3 bg-white rounded-full border border-gray-200 shadow-sm" data-id="element-528"></div>}
                    </div>
                    {/* Location name label */}
                    <div className={`absolute -bottom-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap px-2 py-0.5 rounded-full bg-white/80 text-xs font-medium shadow-sm transition-opacity duration-300
                        ${selectedLocation === location.id ? 'opacity-100' : 'opacity-70'}`} style={{
                  color: `var(--${location.color})`
                }} data-id="element-529">
                      {location.id === 'thailand' ? 'Thailand' : location.id === 'finland' ? 'Finland' : 'Singapore'}
                    </div>
                  </div>
                </div>)}
              {/* Location Detail Panel - Appears when a location is selected */}
              {selectedLocation && <div className="absolute top-4 right-4 w-72 bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-500 animate-fadeIn z-30" data-id="element-530">
                  {/* Selected location data */}
                  {(() => {
                const location = journeyLocations.find(loc => loc.id === selectedLocation);
                if (!location) return null;
                return <>
                        {/* Location image */}
                        <div className="relative h-32 overflow-hidden" data-id="element-531">
                          <img src={location.image} alt={location.name} className="w-full h-full object-cover" data-id="element-532" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-533"></div>
                          {/* Close button */}
                          <button className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/80 flex items-center justify-center text-gray-700 hover:bg-white transition-colors" onClick={e => {
                      e.stopPropagation();
                      setSelectedLocation(null);
                    }} data-id="element-534">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-535">
                              <line x1="18" y1="6" x2="6" y2="18" data-id="element-536"></line>
                              <line x1="6" y1="6" x2="18" y2="18" data-id="element-537"></line>
                            </svg>
                          </button>
                          {/* Location title overlay */}
                          <div className="absolute bottom-2 left-3 right-3 text-white" data-id="element-538">
                            <div className="text-xs font-medium" style={{
                        color: `var(--${location.color})`
                      }} data-id="element-539">
                              {location.years}
                            </div>
                            <h3 className="text-lg font-medium" data-id="element-540">
                              {location.name}
                            </h3>
                          </div>
                        </div>
                        {/* Location details */}
                        <div className="p-4" data-id="element-541">
                          <p className="text-sm text-gray-600 mb-3" data-id="element-542">
                            {location.description}
                          </p>
                          {/* Key experiences */}
                          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-543">
                            Key Experiences
                          </h4>
                          <ul className="text-sm space-y-1 mb-3" data-id="element-544">
                            {location.details.map((detail, idx) => <li key={idx} className="flex items-start" data-id="element-545">
                                <div className="w-1.5 h-1.5 rounded-full mt-1.5 mr-2 flex-shrink-0" style={{
                          backgroundColor: `var(--${location.color})`
                        }} data-id="element-546"></div>
                                <span className="text-gray-700" data-id="element-547">{detail}</span>
                              </li>)}
                          </ul>
                          {/* Learn more button */}
                          <button className="w-full mt-2 py-2 rounded-md text-sm font-medium transition-colors text-white" style={{
                      backgroundColor: `var(--${location.color})`,
                      boxShadow: `0 0 10px rgba(var(--${location.color}-rgb), 0.3)`
                    }} onClick={() => {
                      // Scroll to the relevant chapter section
                      const chapterIndex = location.id === 'thailand' ? 0 : location.id === 'finland' ? 2 : 5; // Singapore is chapter 5
                      scrollToChapter(chapterIndex);
                      setSelectedLocation(null);
                    }} data-id="element-548">
                            Read My Story
                          </button>
                        </div>
                      </>;
              })()}
                </div>}
              {/* Map Controls and Legend */}
              <div className="absolute bottom-4 left-4 bg-white/90 rounded-lg shadow-sm p-3 backdrop-blur-sm z-20" data-id="element-549">
                <h4 className="text-xs font-medium text-gray-700 mb-2" data-id="element-550">
                  My Journey
                </h4>
                <div className="flex flex-col space-y-2" data-id="element-551">
                  <div className="flex items-center" data-id="element-552">
                    <div className="w-3 h-3 bg-coral-pink rounded-full mr-2" data-id="element-553"></div>
                    <span className="text-xs text-grey-600" data-id="element-554">
                      Thailand (Childhood)
                    </span>
                  </div>
                  <div className="flex items-center" data-id="element-555">
                    <div className="w-3 h-3 bg-sage-mint rounded-full mr-2" data-id="element-556"></div>
                    <span className="text-xs text-grey-600" data-id="element-557">
                      Finland (Education)
                    </span>
                  </div>
                  <div className="flex items-center" data-id="element-558">
                    <div className="w-3 h-3 bg-blush-rose rounded-full mr-2" data-id="element-559"></div>
                    <span className="text-xs text-grey-600" data-id="element-560">
                      Singapore (Future)
                    </span>
                  </div>
                </div>
              </div>
              {/* Interactive instruction */}
              <div className="absolute top-4 left-4 bg-white/90 rounded-lg shadow-sm px-4 py-2 backdrop-blur-sm text-xs text-grey-600 z-20 flex items-center" data-id="element-561">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1.5" data-id="element-562">
                  <circle cx="12" cy="12" r="10" data-id="element-563"></circle>
                  <path d="M12 16v-4" data-id="element-564"></path>
                  <path d="M12 8h.01" data-id="element-565"></path>
                </svg>
                Click on markers to explore my journey
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Photo carousel */}
      <div className="py-16" data-id="element-566">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-567">
          <h2 className="text-2xl md:text-3xl font-light text-charcoal mb-8 text-center" data-id="element-568">
            Life Across Cultures
          </h2>
          <div className="relative" data-id="element-569">
            <div className="liquid-glass-card overflow-hidden rounded-xl shadow-card hover:shadow-glow-mint transition-all duration-300" data-id="element-570">
              <div className="glass-reflection" data-id="element-571"></div>
              <div className="flex transition-transform duration-500 ease-in-out" style={{
              transform: `translateX(-${currentPhotoIndex * 100}%)`
            }} data-id="element-572">
                {photos.map((photo, index) => <div key={index} className="min-w-full relative" data-id="element-573">
                    <img src={photo.src} alt={photo.caption} className="w-full h-[400px] object-cover" data-id="element-574" />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-charcoal/70 to-transparent h-1/3" data-id="element-575"></div>
                    <div className="absolute bottom-4 left-4 right-4 text-white" data-id="element-576">
                      <p className="text-lg font-medium" data-id="element-577">{photo.caption}</p>
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Navigation buttons */}
            <button onClick={prevPhoto} className="liquid-glass-button absolute top-1/2 left-4 transform -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 flex items-center justify-center shadow-card hover:bg-white hover:shadow-glow-mint hover:scale-110 active:scale-95 transition-all duration-300" data-id="element-578">
              <ArrowRight className="h-5 w-5 text-charcoal transform rotate-180" data-id="element-579" />
            </button>
            <button onClick={nextPhoto} className="liquid-glass-button absolute top-1/2 right-4 transform -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 flex items-center justify-center shadow-card hover:bg-white hover:shadow-glow-mint hover:scale-110 active:scale-95 transition-all duration-300" data-id="element-580">
              <ArrowRight className="h-5 w-5 text-charcoal" data-id="element-581" />
            </button>
            {/* Dots indicators */}
            <div className="flex justify-center mt-4 space-x-2" data-id="element-582">
              {photos.map((_, index) => <button key={index} onClick={() => setCurrentPhotoIndex(index)} className={`w-2 h-2 rounded-full transition-all duration-300 ${currentPhotoIndex === index ? 'bg-sage-mint w-4' : 'bg-grey-400/50 hover:bg-sage-mint/50'}`} data-id="element-583"></button>)}
            </div>
          </div>
        </div>
      </div>
      {/* Cultural bridge visual */}
      <div className="py-16 bg-sage-mint/5" data-id="element-584">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-585">
          <h2 className="text-2xl md:text-3xl font-light text-charcoal mb-8 text-center" data-id="element-586">
            Bridging Two Healthcare Worlds
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-587">
            {/* Thai/Asian healthcare values */}
            <div className="liquid-glass-card bg-coral-pink/10 rounded-xl p-6 transition-all duration-300 hover:shadow-glow-coral relative overflow-hidden group cursor-pointer" onClick={e => triggerSubtleConfetti(e.currentTarget)} data-id="element-588">
              <div className="glass-reflection" data-id="element-589"></div>
              <div className="absolute top-0 right-0 w-24 h-24 bg-coral-pink/20 rounded-full -translate-x-1/3 -translate-y-1/3 group-hover:scale-125 transition-transform duration-500" data-id="element-590"></div>
              <h3 className="text-xl font-medium text-charcoal mb-4 relative z-10" data-id="element-591">
                Thai & Southeast Asian Approach
              </h3>
              <ul className="space-y-3 relative z-10" data-id="element-592">
                <li className="flex items-start" data-id="element-593">
                  <Heart className="h-5 w-5 text-coral-pink mr-2 flex-shrink-0 mt-0.5" data-id="element-594" />
                  <span className="text-grey-600" data-id="element-595">Family-centered care</span>
                </li>
                <li className="flex items-start" data-id="element-596">
                  <Heart className="h-5 w-5 text-coral-pink mr-2 flex-shrink-0 mt-0.5" data-id="element-597" />
                  <span className="text-grey-600" data-id="element-598">
                    Community support systems
                  </span>
                </li>
                <li className="flex items-start" data-id="element-599">
                  <Heart className="h-5 w-5 text-coral-pink mr-2 flex-shrink-0 mt-0.5" data-id="element-600" />
                  <span className="text-grey-600" data-id="element-601">Respect for elders</span>
                </li>
                <li className="flex items-start" data-id="element-602">
                  <Heart className="h-5 w-5 text-coral-pink mr-2 flex-shrink-0 mt-0.5" data-id="element-603" />
                  <span className="text-grey-600" data-id="element-604">
                    Holistic wellness perspective
                  </span>
                </li>
              </ul>
            </div>
            {/* Mona's unique synthesis */}
            <div className="liquid-glass-card bg-white rounded-xl p-6 shadow-card transition-all duration-300 hover:shadow-glow-mint relative overflow-hidden group cursor-pointer" onClick={e => triggerSubtleConfetti(e.currentTarget)} data-id="element-605">
              <div className="glass-reflection" data-id="element-606"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-coral-pink/5 to-sage-mint/5" data-id="element-607"></div>
              <h3 className="text-xl font-medium text-charcoal mb-4 relative z-10 text-center" data-id="element-608">
                Mona's Unique Synthesis
              </h3>
              <div className="flex justify-center mb-6 relative z-10" data-id="element-609">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-coral-pink/30 to-sage-mint/30 flex items-center justify-center" data-id="element-610">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center" data-id="element-611">
                    <Heart className="h-6 w-6 text-charcoal" data-id="element-612" />
                  </div>
                </div>
              </div>
              <ul className="space-y-3 relative z-10" data-id="element-613">
                <li className="flex items-start" data-id="element-614">
                  <ArrowRight className="h-4 w-4 text-sage-mint mr-2 flex-shrink-0 mt-0.5" data-id="element-615" />
                  <span className="text-grey-600" data-id="element-616">
                    Culturally adaptive communication
                  </span>
                </li>
                <li className="flex items-start" data-id="element-617">
                  <ArrowRight className="h-4 w-4 text-sage-mint mr-2 flex-shrink-0 mt-0.5" data-id="element-618" />
                  <span className="text-grey-600" data-id="element-619">
                    Evidence-based compassion
                  </span>
                </li>
                <li className="flex items-start" data-id="element-620">
                  <ArrowRight className="h-4 w-4 text-sage-mint mr-2 flex-shrink-0 mt-0.5" data-id="element-621" />
                  <span className="text-grey-600" data-id="element-622">
                    Multilingual patient advocacy
                  </span>
                </li>
                <li className="flex items-start" data-id="element-623">
                  <ArrowRight className="h-4 w-4 text-sage-mint mr-2 flex-shrink-0 mt-0.5" data-id="element-624" />
                  <span className="text-grey-600" data-id="element-625">Precision with warmth</span>
                </li>
              </ul>
            </div>
            {/* Finnish/Nordic healthcare values */}
            <div className="liquid-glass-card bg-sage-mint/10 rounded-xl p-6 transition-all duration-300 hover:shadow-glow-mint relative overflow-hidden group cursor-pointer" onClick={e => triggerSubtleConfetti(e.currentTarget)} data-id="element-626">
              <div className="glass-reflection" data-id="element-627"></div>
              <div className="absolute top-0 left-0 w-24 h-24 bg-sage-mint/20 rounded-full translate-x-1/3 -translate-y-1/3 group-hover:scale-125 transition-transform duration-500" data-id="element-628"></div>
              <h3 className="text-xl font-medium text-charcoal mb-4 relative z-10" data-id="element-629">
                Finnish & Nordic Approach
              </h3>
              <ul className="space-y-3 relative z-10" data-id="element-630">
                <li className="flex items-start" data-id="element-631">
                  <Heart className="h-5 w-5 text-sage-mint mr-2 flex-shrink-0 mt-0.5" data-id="element-632" />
                  <span className="text-grey-600" data-id="element-633">
                    Evidence-based protocols
                  </span>
                </li>
                <li className="flex items-start" data-id="element-634">
                  <Heart className="h-5 w-5 text-sage-mint mr-2 flex-shrink-0 mt-0.5" data-id="element-635" />
                  <span className="text-grey-600" data-id="element-636">Patient autonomy</span>
                </li>
                <li className="flex items-start" data-id="element-637">
                  <Heart className="h-5 w-5 text-sage-mint mr-2 flex-shrink-0 mt-0.5" data-id="element-638" />
                  <span className="text-grey-600" data-id="element-639">
                    Technological integration
                  </span>
                </li>
                <li className="flex items-start" data-id="element-640">
                  <Heart className="h-5 w-5 text-sage-mint mr-2 flex-shrink-0 mt-0.5" data-id="element-641" />
                  <span className="text-grey-600" data-id="element-642">
                    Systematic quality improvement
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      {/* Closing section */}
      <div className="py-16" data-id="element-643">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-644">
          <Quote className="h-8 w-8 text-sage-mint mx-auto mb-4" data-id="element-645" />
          <p className="text-xl md:text-2xl text-grey-600 italic mb-8" data-id="element-646">
            "Given the chance to collaborate with you, I promise this: I'll
            bring not just my hands and mind to your wards, but my whole heart—a
            heart that beats in three languages but speaks the universal
            language of compassionate care."
          </p>
          <div className="liquid-glass-button inline-flex items-center bg-sage-mint/90 text-white rounded-full px-8 py-4 transition-all duration-300 transform hover:scale-105 hover:shadow-glow-mint active:scale-98 cursor-pointer overflow-hidden relative" onClick={e => triggerSubtleConfetti(e.currentTarget)} data-id="element-647">
            <span className="relative z-10" data-id="element-648">Connect With Me</span>
            <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none" style={{
            background: `radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0) 60%)`
          }} data-id="element-649" />
          </div>
        </div>
      </div>
      {/* Read more section */}
      <div className="py-8 bg-sage-mint/5 border-t border-sage-mint/20" data-id="element-650">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-651">
          <div className="liquid-glass-card bg-white/80 rounded-xl p-6 shadow-card hover:shadow-glow-mint transition-all duration-300" data-id="element-652">
            <div className="glass-reflection" data-id="element-653"></div>
            <div className="flex flex-col md:flex-row md:items-center justify-between" data-id="element-654">
              <h3 className="text-xl font-light text-charcoal" data-id="element-655">
                Explore my{' '}
                <span className="font-medium" data-id="element-656">Clinical Expertise</span> and{' '}
                <span className="font-medium" data-id="element-657">Certifications</span>
              </h3>
              <div className="mt-4 md:mt-0 flex space-x-4" data-id="element-658">
                <a href="#expertise" className="liquid-glass-button bg-coral-pink/80 text-white px-5 py-2 rounded-full hover:shadow-glow-coral hover:scale-105 transition-all duration-300 flex items-center justify-center relative overflow-hidden" onClick={e => triggerSubtleConfetti(e.currentTarget)} data-id="element-659">
                  <span className="relative z-10" data-id="element-660">Clinical Skills</span>
                  <ChevronDown className="h-4 w-4 ml-1 relative z-10" data-id="element-661" />
                </a>
                <a href="#certifications" className="liquid-glass-button bg-sage-mint/80 text-white px-5 py-2 rounded-full hover:shadow-glow-mint hover:scale-105 transition-all duration-300 flex items-center justify-center relative overflow-hidden" onClick={e => triggerSubtleConfetti(e.currentTarget)} data-id="element-662">
                  <span className="relative z-10" data-id="element-663">Certifications</span>
                  <ChevronDown className="h-4 w-4 ml-1 relative z-10" data-id="element-664" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default JourneyStory;